import {
  MaskHistory,
  applyBrushStroke,
  applyMaskAlpha,
  composeExportBuffer,
  decontaminateEdges as decontaminateEdgesSync,
  decontaminateEdgesLocal,
  featherMask as featherMaskSync,
  chokeExpandMask as chokeExpandMaskSync,
} from '@myphototools/core/bgremove';
import type { MaskImage, RgbColor as CoreRgbColor } from '@myphototools/core/bgremove';
import { CanvasView } from './canvasView.js';
import { FileManager, setupDropzone, renderFileList } from './fileManager.js';
import { WorkerClient } from '../workers/workerClient.js';
import { decodeImageToRgba, encodeRgbaToBlob } from '../io/imageCodec.js';
import { buildZip, downloadBlob } from '../io/zip.js';
import { announceCancelled, announceSaved, bindSaveLocation, saveLocationMarkup } from './saveLocation.js';
import { showToast } from './toast.js';
import { withProgress } from './progress.js';
import type { BgTool, LoadedFile, PreviewBackground } from '../types.js';

interface RasterState {
  width: number;
  height: number;
  original: Uint8ClampedArray;
  dpi: { x: number; y: number } | null;
}

export function mountBgRemoveTab(panel: HTMLElement): void {
  panel.innerHTML = '';
  panel.appendChild(buildMarkup());

  const fileManager = new FileManager();
  const bgWorker = new WorkerClient(new Worker(new URL('../workers/bg.worker.ts', import.meta.url), { type: 'module' }));
  const dpiWorker = new WorkerClient(new Worker(new URL('../workers/dpi.worker.ts', import.meta.url), { type: 'module' }));
  const mlWorker = new WorkerClient(new Worker(new URL('../workers/ml.worker.ts', import.meta.url), { type: 'module' }));
  const pageHref = document.baseURI;

  const canvasViewport = panel.querySelector('.canvas-viewport') as HTMLDivElement;
  const canvasView = new CanvasView(canvasViewport);

  const el = <T extends HTMLElement>(sel: string) => panel.querySelector(sel) as T;
  const dropzone = el<HTMLDivElement>('#bg-dropzone');
  const fileList = el<HTMLUListElement>('#bg-file-list');
  const dimsReadout = el<HTMLDivElement>('#bg-dims-readout');

  const toolButtons = {
    move: el<HTMLButtonElement>('#tool-move'),
    wand: el<HTMLButtonElement>('#tool-wand'),
    colorKey: el<HTMLButtonElement>('#tool-colorkey'),
    eraseBrush: el<HTMLButtonElement>('#tool-erase'),
    restoreBrush: el<HTMLButtonElement>('#tool-restore'),
  } satisfies Record<BgTool, HTMLButtonElement>;
  const redetectBtn = el<HTMLButtonElement>('#redetect-bg');
  const retryBtn = el<HTMLButtonElement>('#retry-bg');
  const zoomOutBtn = el<HTMLButtonElement>('#zoom-out-bg');
  const zoomInBtn = el<HTMLButtonElement>('#zoom-in-bg');
  const zoomResetBtn = el<HTMLButtonElement>('#zoom-reset-bg');
  const zoomLevelOut = el<HTMLOutputElement>('#zoom-level-bg');
  const updateZoomUi = (percent: number) => {
    zoomLevelOut.textContent = `${percent}%`;
    zoomOutBtn.disabled = !canvasView.canZoomOut();
    zoomInBtn.disabled = !canvasView.canZoomIn();
  };
  canvasView.setOnZoomChange(updateZoomUi);
  zoomInBtn.addEventListener('click', () => canvasView.zoomIn());
  zoomOutBtn.addEventListener('click', () => canvasView.zoomOut());
  zoomResetBtn.addEventListener('click', () => canvasView.zoomReset());
  updateZoomUi(canvasView.getZoomPercent());

  const qualitySel = el<HTMLSelectElement>('#bg-quality');
  const qualityHint = el<HTMLParagraphElement>('#bg-quality-hint');
  const detailInput = el<HTMLInputElement>('#bg-detail');
  const detailOut = el<HTMLOutputElement>('#bg-detail-out');
  const cleanEdgesToggle = el<HTMLInputElement>('#bg-clean-edges');
  let qualityTouched = false;
  // Start loading the model immediately so the first image doesn't pay the cold-start cost.
  // Without WebGPU the best-detail model runs on WASM at tens of seconds per photo, so default
  // such devices to the fast model (the user can still pick Best detail explicitly).
  void mlWorker
    .call<{ ready: boolean; webgpu: boolean }>('warmUp', { quality: 'anime', pageHref })
    .then((res) => {
      if (!res.webgpu && !qualityTouched) {
        qualitySel.value = 'fast';
        qualityHint.hidden = false;
      }
    })
    .catch(() => {});
  const toleranceInput = el<HTMLInputElement>('#bg-tolerance');
  const toleranceOut = el<HTMLOutputElement>('#bg-tolerance-out');
  const wandModeSeg = el<HTMLDivElement>('#wand-mode');
  const wandActionSeg = el<HTMLDivElement>('#wand-action');
  const brushSizeInput = el<HTMLInputElement>('#brush-size');
  const brushSizeOut = el<HTMLOutputElement>('#brush-size-out');
  const brushHardnessInput = el<HTMLInputElement>('#brush-hardness');
  const brushHardnessOut = el<HTMLOutputElement>('#brush-hardness-out');
  const featherInput = el<HTMLInputElement>('#feather');
  const featherOut = el<HTMLOutputElement>('#feather-out');
  const chokeInput = el<HTMLInputElement>('#choke-expand');
  const chokeOut = el<HTMLOutputElement>('#choke-expand-out');
  const decontamToggle = el<HTMLInputElement>('#decontaminate');

  const bgPreviewSeg = el<HTMLDivElement>('#preview-bg');
  const customBgColorInput = el<HTMLInputElement>('#preview-bg-custom');
  const beforeAfterBtn = el<HTMLButtonElement>('#before-after-toggle');
  const undoBtn = el<HTMLButtonElement>('#bg-undo');
  const redoBtn = el<HTMLButtonElement>('#bg-redo');

  const outFormatSel = el<HTMLSelectElement>('#bg-out-format');
  const jpegQualityInput = el<HTMLInputElement>('#bg-jpeg-quality');
  const jpegQualityOut = el<HTMLOutputElement>('#bg-jpeg-quality-out');
  const jpegQualityRow = el<HTMLDivElement>('#bg-jpeg-quality-row');
  const fillColorInput = el<HTMLInputElement>('#bg-fill-color');
  const fillColorRow = el<HTMLDivElement>('#bg-fill-color-row');
  const suffixInput = el<HTMLInputElement>('#bg-suffix');
  const exportCurrentBtn = el<HTMLButtonElement>('#bg-export-current');
  const goBtn = el<HTMLButtonElement>('#go-bg');
  const autoStartToggle = el<HTMLInputElement>('#bg-auto-start');
  bindSaveLocation(panel, 'bg');
  const AUTO_START_KEY = 'mpt.autoStart';
  try {
    autoStartToggle.checked = localStorage.getItem(AUTO_START_KEY) === '1';
  } catch {
    // no storage: default off
  }
  autoStartToggle.addEventListener('change', () => {
    try {
      localStorage.setItem(AUTO_START_KEY, autoStartToggle.checked ? '1' : '0');
    } catch {
      // ignore
    }
  });
  /** True once the model has run (or been skipped) for the loaded photo; GO shows until then. */
  let detectionDone = false;
  function setGoVisible(visible: boolean): void {
    goBtn.hidden = !visible;
    redetectBtn.hidden = visible;
    retryBtn.hidden = visible;
  }
  const exportAllBtn = el<HTMLButtonElement>('#bg-export-all');

  let raster: RasterState | null = null;
  let baseMask: MaskImage | null = null;
  /** Model output for the current image, kept so the detail slider can re-refine without re-running the model. */
  let coarse: { width: number; height: number; data: Float32Array } | null = null;
  /** Bumped on every load/unload so a slow load that finishes after its file was closed is ignored. */
  let loadToken = 0;
  let history = new MaskHistory(50);
  let activeTool: BgTool = 'wand';
  /** "W × Hpx · N DPI" for the current image; the model/timing summary is appended after each run. */
  let dimsText = '';
  let colorKeyPickedColor: CoreRgbColor | null = null;
  let showingBefore = false;
  let isDrawing = false;
  let lastBrushPoint: { x: number; y: number } | null = null;

  function setActiveTool(tool: BgTool): void {
    activeTool = tool;
    canvasView.setPanTool(tool === 'move');
    canvasView.setClickTool(tool === 'wand' || tool === 'colorKey');
    for (const [key, btn] of Object.entries(toolButtons)) {
      btn.setAttribute('aria-pressed', String(key === tool));
    }
  }

  function deriveFinalMask(): MaskImage {
    if (!baseMask) throw new Error('No image loaded');
    const featherPx = Number(featherInput.value);
    const chokePx = Number(chokeInput.value);
    let mask = featherPx > 0 ? featherMaskSync(baseMask, featherPx) : baseMask;
    if (chokePx !== 0) mask = chokeExpandMaskSync(mask, chokePx);
    return mask;
  }

  /**
   * Un-mixes background colour out of the semi-transparent edge pixels. With a colour-keyed
   * background we know the exact colour; otherwise it's estimated locally around each edge
   * pixel, which also copes with gradients and busy backgrounds.
   */
  function decontaminate(rgba: Uint8ClampedArray, mask: MaskImage): Uint8ClampedArray {
    if (colorKeyPickedColor) return decontaminateEdgesSync(rgba, mask, colorKeyPickedColor);
    return decontaminateEdgesLocal(rgba, mask);
  }

  /**
   * `fast` skips the per-frame edge passes (feather/choke/fringe removal) so brush strokes stay
   * responsive on large photos; the full-quality render follows when the stroke ends.
   */
  function renderPreview(fast = false): void {
    if (!raster) return;
    if (showingBefore) {
      canvasView.drawImage(raster.original);
      return;
    }
    const mask = fast ? baseMask! : deriveFinalMask();
    let rgba = raster.original;
    if (decontamToggle.checked && !fast) rgba = decontaminate(raster.original, mask);
    const composed = applyMaskAlpha(rgba, mask);
    canvasView.drawImage(composed);
    undoBtn.disabled = !history.canUndo();
    redoBtn.disabled = !history.canRedo();
  }

  function commitMask(newMask: MaskImage): void {
    if (!baseMask) return;
    history.push(baseMask);
    baseMask = newMask;
    renderPreview();
  }

  interface SegmentResult {
    alpha: ArrayBuffer;
    width: number;
    height: number;
    coarse: { width: number; height: number; data: ArrayBuffer };
    model: 'best' | 'anime' | 'fast';
    provider: 'webgpu' | 'wasm';
    modelMs: number;
    refineMs: number;
  }

  /** Runs the on-device segmentation model and returns its mask, or null if it failed. */
  async function autoSegment(
    fileRaster: RasterState,
    onDone?: (res: SegmentResult) => void,
    onProgress?: (done: number, total: number) => void,
    mode: 'normal' | 'thorough' = 'normal',
  ): Promise<MaskImage | null> {
    try {
      const res = await mlWorker.call<SegmentResult>(
        'autoSegment',
        {
          rgba: fileRaster.original.buffer.slice(0),
          width: fileRaster.width,
          height: fileRaster.height,
          quality: qualitySel.value === 'fast' ? 'fast' : qualitySel.value === 'anime' ? 'anime' : 'best',
          refine: true,
          detail: Number(detailInput.value),
          mode,
          cleanEdges: cleanEdgesToggle.checked,
          pageHref,
        },
        [],
        onProgress,
      );
      if (fileRaster === raster) coarse = { width: res.coarse.width, height: res.coarse.height, data: new Float32Array(res.coarse.data) };
      onDone?.(res);
      return { width: res.width, height: res.height, alpha: new Uint8ClampedArray(res.alpha) };
    } catch (err) {
      showToast(err instanceof Error ? `Automatic background removal failed: ${err.message}` : 'Automatic background removal failed', true);
      return null;
    }
  }

  /** Drops the current image and resets every bit of per-image state, leaving an empty workspace. */
  function unloadImage(): void {
    loadToken++;
    raster = null;
    baseMask = null;
    coarse = null;
    history = new MaskHistory(50);
    colorKeyPickedColor = null;
    showingBefore = false;
    isDrawing = false;
    lastBrushPoint = null;
    beforeAfterBtn.setAttribute('aria-pressed', 'false');
    canvasView.clear();
    detectionDone = false;
    setGoVisible(false);
    dimsText = '';
    dimsReadout.textContent = '';
    dimsReadout.title = '';
    undoBtn.disabled = true;
    redoBtn.disabled = true;
  }

  async function loadFile(file: LoadedFile): Promise<void> {
    const token = ++loadToken;
    try {
      if (file.sourceFormat === 'pdf') {
        const dpi = 150;
        const result = await withProgress('Rasterizing PDF page…', async (onProgress) => {
          return dpiWorker.call<{ width: number; height: number; rgba: ArrayBuffer; pageNumber: number; dpi: number }[]>(
            'rasterizePdf',
            { bytes: file.originalBytes.slice(0), dpi, pageNumbers: [file.pdfPageNumber ?? 1] },
            [],
            onProgress,
          );
        });
        const page = result[0];
        if (token !== loadToken) return; // file was closed or switched while rasterizing
        raster = { width: page.width, height: page.height, original: new Uint8ClampedArray(page.rgba), dpi: { x: dpi, y: dpi } };
      } else {
        const blob = new Blob([file.originalBytes], { type: file.sourceFormat === 'png' ? 'image/png' : 'image/jpeg' });
        const decoded = await decodeImageToRgba(blob);
        const dpiInfo = await dpiWorker.call<{ dpi: { x: number; y: number } | null }>('readDpi', { bytes: file.originalBytes.slice(0) });
        if (token !== loadToken) return;
        raster = { width: decoded.width, height: decoded.height, original: decoded.rgba, dpi: dpiInfo.dpi };
      }
      coarse = null;
      history = new MaskHistory(50);
      colorKeyPickedColor = null;
      showingBefore = false;
      beforeAfterBtn.setAttribute('aria-pressed', 'false');
      canvasView.setImage(raster.width, raster.height, raster.original);
      dimsText = `${raster.width} × ${raster.height}px${raster.dpi ? ` · ${Math.round(raster.dpi.x)} DPI` : ''}`;
      dimsReadout.textContent = dimsText;

      // The photo shows as-is first. Background removal starts when the user presses GO (or at
      // once when "Start automatically" is on). Manual tools are for touching up what the model got wrong.
      const fallback: MaskImage = { width: raster.width, height: raster.height, alpha: new Uint8ClampedArray(raster.width * raster.height).fill(255) };
      baseMask = fallback;
      detectionDone = false;
      renderPreview();
      if (!autoStartToggle.checked) {
        setGoVisible(true);
        dimsReadout.textContent = `${dimsText} · press GO to remove the background`;
        return;
      }
      await runDetection(token);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load file', true);
    }
  }

  /** Runs the automatic cutout on the loaded photo (first pass after loading / GO). */
  async function runDetection(token: number): Promise<void> {
    if (!raster) return;
    const loading = raster;
    setGoVisible(false);
    dimsReadout.textContent = dimsText;
    const detected = await withProgress('Removing background…', (onProgress) => autoSegment(loading, describeRun, onProgress));
    if (token !== loadToken || raster !== loading) return; // closed/switched while the model ran
    detectionDone = true;
    if (detected) baseMask = detected;
    renderPreview();
  }
  goBtn.addEventListener('click', () => {
    if (!raster || detectionDone) return;
    void runDetection(loadToken);
  });

  /** Appends which model ran (and how long it took) to the dimensions readout. */
  function describeRun(res: SegmentResult): void {
    const modelName = res.model === 'best' ? 'BiRefNet' : res.model === 'anime' ? 'AnimeSeg' : 'U²-Net';
    const secs = ((res.modelMs + res.refineMs) / 1000).toFixed(1);
    dimsReadout.textContent = `${dimsText} · ${modelName} (${res.provider === 'webgpu' ? 'GPU' : 'CPU'}) ${secs}s`;
    dimsReadout.title = `Model ${(res.modelMs / 1000).toFixed(1)}s · edge refinement ${(res.refineMs / 1000).toFixed(1)}s`;
    if (qualitySel.value !== 'fast' && res.model !== qualitySel.value) {
      showToast('The selected model could not run on this device; a fallback model was used instead', true);
    }
  }

  fileManager.onChange = (files, activeId) => {
    renderFileList(fileList, files, activeId, (id) => fileManager.setActive(id), (id) => fileManager.remove(id));
    exportAllBtn.disabled = files.length < 2;
    const active = fileManager.getActive();
    if (active) void loadFile(active);
    else unloadImage();
  };

  setupDropzone(dropzone, (files) => {
    fileManager.addFiles(files).catch((err) => showToast(err.message, true));
  });

  for (const [tool, btn] of Object.entries(toolButtons)) {
    btn.addEventListener('click', () => setActiveTool(tool as BgTool));
  }
  setActiveTool('wand');

  toleranceInput.addEventListener('input', () => (toleranceOut.textContent = `${toleranceInput.value}%`));
  brushSizeInput.addEventListener('input', () => (brushSizeOut.textContent = `${brushSizeInput.value}px`));
  brushHardnessInput.addEventListener('input', () => (brushHardnessOut.textContent = `${brushHardnessInput.value}%`));
  featherInput.addEventListener('input', () => {
    featherOut.textContent = `${featherInput.value}px`;
    renderPreview();
  });
  chokeInput.addEventListener('input', () => {
    chokeOut.textContent = `${chokeInput.value}px`;
    renderPreview();
  });
  decontamToggle.addEventListener('change', () => renderPreview());

  for (const seg of [wandModeSeg, wandActionSeg]) {
    for (const btn of Array.from(seg.querySelectorAll('button'))) {
      btn.addEventListener('click', () => {
        for (const b of Array.from(seg.querySelectorAll('button'))) b.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-pressed', 'true');
      });
    }
  }

  for (const btn of Array.from(bgPreviewSeg.querySelectorAll('button'))) {
    btn.addEventListener('click', () => {
      for (const b of Array.from(bgPreviewSeg.querySelectorAll('button'))) b.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-pressed', 'true');
      canvasView.setBackground(btn.dataset.bg as PreviewBackground, hexToRgb(customBgColorInput.value));
    });
  }
  customBgColorInput.addEventListener('input', () => {
    canvasView.setBackground('custom', hexToRgb(customBgColorInput.value));
    for (const b of Array.from(bgPreviewSeg.querySelectorAll('button'))) b.setAttribute('aria-pressed', String(b.dataset.bg === 'custom'));
  });

  beforeAfterBtn.addEventListener('click', () => {
    showingBefore = !showingBefore;
    beforeAfterBtn.setAttribute('aria-pressed', String(showingBefore));
    renderPreview();
  });

  undoBtn.addEventListener('click', () => {
    if (!baseMask) return;
    const prev = history.undo(baseMask);
    if (prev) {
      baseMask = prev;
      renderPreview();
    }
  });
  redoBtn.addEventListener('click', () => {
    if (!baseMask) return;
    const next = history.redo(baseMask);
    if (next) {
      baseMask = next;
      renderPreview();
    }
  });

  outFormatSel.addEventListener('change', updateOutputVisibility);
  function updateOutputVisibility(): void {
    const format = outFormatSel.value;
    jpegQualityRow.hidden = format !== 'jpeg';
    fillColorRow.hidden = format === 'png'; // PNG fill is optional via a checkbox baked into the color row label; JPEG always needs it
  }
  updateOutputVisibility();
  jpegQualityInput.addEventListener('input', () => (jpegQualityOut.textContent = jpegQualityInput.value));

  function hexToRgb(hex: string): CoreRgbColor {
    const v = parseInt(hex.replace('#', ''), 16);
    return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
  }

  canvasView.setToolHandler((point, event, phase) => {
    if (!raster || !baseMask) return;
    const { x, y } = point;
    if (x < 0 || y < 0 || x >= raster.width || y >= raster.height) return;

    if (activeTool === 'wand' && phase === 'down') {
      const mode = (wandModeSeg.querySelector('[aria-pressed="true"]') as HTMLButtonElement)?.dataset.mode === 'global' ? 'global' : 'contiguous';
      void bgWorker
        .call<{ alpha: ArrayBuffer }>('magicWand', {
          rgba: raster.original.buffer.slice(0),
          width: raster.width,
          height: raster.height,
          x,
          y,
          tolerance: Number(toleranceInput.value),
          mode,
        })
        .then((res) => {
          const selection = new Uint8ClampedArray(res.alpha);
          const restore = (wandActionSeg.querySelector('[aria-pressed="true"]') as HTMLButtonElement)?.dataset.action === 'restore';
          const merged = restore ? mergeRestore(baseMask!, selection) : mergeRemoval(baseMask!, selection);
          commitMask(merged);
        })
        .catch((err) => showToast(err.message, true));
      return;
    }

    if (activeTool === 'colorKey' && phase === 'down') {
      const idx = (y * raster.width + x) * 4;
      colorKeyPickedColor = { r: raster.original[idx], g: raster.original[idx + 1], b: raster.original[idx + 2] };
      void bgWorker
        .call<{ alpha: ArrayBuffer }>('colorKey', {
          rgba: raster.original.buffer.slice(0),
          width: raster.width,
          height: raster.height,
          color: colorKeyPickedColor,
          tolerance: Number(toleranceInput.value),
        })
        .then((res) => {
          const selection = new Uint8ClampedArray(res.alpha);
          const merged = mergeRemoval(baseMask!, selection);
          commitMask(merged);
        })
        .catch((err) => showToast(err.message, true));
      return;
    }

    if (activeTool === 'eraseBrush' || activeTool === 'restoreBrush') {
      const mode = activeTool === 'eraseBrush' ? 'erase' : 'restore';
      const radius = Number(brushSizeInput.value);
      const hardness = Number(brushHardnessInput.value) / 100;
      if (phase === 'down') {
        history.push(baseMask);
        isDrawing = true;
        lastBrushPoint = { x, y };
        applyBrushStroke(baseMask, { x, y }, { x, y }, radius, hardness, mode);
        renderPreview(true);
      } else if (phase === 'move' && isDrawing && lastBrushPoint) {
        applyBrushStroke(baseMask, lastBrushPoint, { x, y }, radius, hardness, mode);
        lastBrushPoint = { x, y };
        renderPreview(true);
      } else if (phase === 'up') {
        if (isDrawing) renderPreview();
        isDrawing = false;
        lastBrushPoint = null;
      }
    }
  });

  function mergeRemoval(current: MaskImage, selection: Uint8ClampedArray): MaskImage {
    const alpha = new Uint8ClampedArray(current.alpha.length);
    for (let i = 0; i < alpha.length; i++) alpha[i] = selection[i] === 0 ? 0 : current.alpha[i];
    return { width: current.width, height: current.height, alpha };
  }

  /** Brings the selected (similar-colour) region back into the subject, e.g. an outfit the model dropped. */
  function mergeRestore(current: MaskImage, selection: Uint8ClampedArray): MaskImage {
    const alpha = new Uint8ClampedArray(current.alpha.length);
    for (let i = 0; i < alpha.length; i++) alpha[i] = selection[i] === 0 ? 255 : current.alpha[i];
    return { width: current.width, height: current.height, alpha };
  }

  redetectBtn.addEventListener('click', () => {
    if (!raster || !baseMask) return;
    void withProgress('Removing background…', (onProgress) => autoSegment(raster!, describeRun, onProgress)).then((detected) => {
      if (detected) commitMask(detected);
    });
  });
  retryBtn.addEventListener('click', () => {
    if (!raster || !baseMask) return;
    void withProgress('Looking again (thorough)…', (onProgress) => autoSegment(raster!, describeRun, onProgress, 'thorough')).then((detected) => {
      if (detected) commitMask(detected);
    });
  });
  // Switching quality re-runs the cutout so the choice takes effect immediately.
  qualitySel.addEventListener('change', () => {
    qualityTouched = true;
    if (raster && baseMask) redetectBtn.click();
  });

  // The detail amount only changes the refinement step, so it re-applies to the model's cached
  // output without re-running the model (debounced while the slider is being dragged).
  let detailTimer: ReturnType<typeof setTimeout> | undefined;
  let detailRunning = false;
  let detailPending = false;
  async function applyDetail(): Promise<void> {
    if (!raster || !coarse) return;
    if (detailRunning) {
      detailPending = true;
      return;
    }
    detailRunning = true;
    const forRaster = raster;
    const forCoarse = coarse;
    try {
      const res = await mlWorker.call<{ alpha: ArrayBuffer }>('refineMask', {
        rgba: forRaster.original.buffer.slice(0),
        width: forRaster.width,
        height: forRaster.height,
        coarse: { width: forCoarse.width, height: forCoarse.height, data: forCoarse.data.buffer.slice(0) },
        detail: Number(detailInput.value),
        cleanEdges: cleanEdgesToggle.checked,
      });
      if (raster === forRaster && baseMask) commitMask({ width: forRaster.width, height: forRaster.height, alpha: new Uint8ClampedArray(res.alpha) });
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not apply detail setting', true);
    } finally {
      detailRunning = false;
      if (detailPending) {
        detailPending = false;
        void applyDetail();
      }
    }
  }
  cleanEdgesToggle.addEventListener('change', () => void applyDetail());
  detailInput.addEventListener('input', () => {
    detailOut.textContent = `${detailInput.value}%`;
    clearTimeout(detailTimer);
    detailTimer = setTimeout(() => void applyDetail(), 200);
  });

  async function exportFile(fileRaster: RasterState, fileBaseMask: MaskImage, originalName: string): Promise<{ filename: string; blob: Blob }> {
    const featherPx = Number(featherInput.value);
    const chokePx = Number(chokeInput.value);
    let mask = featherPx > 0 ? featherMaskSync(fileBaseMask, featherPx) : fileBaseMask;
    if (chokePx !== 0) mask = chokeExpandMaskSync(mask, chokePx);

    let rgba = fileRaster.original;
    if (decontamToggle.checked) rgba = decontaminate(rgba, mask);

    const format = outFormatSel.value as 'png' | 'jpeg';
    const wantsFill = format === 'jpeg' || fillColorInput.dataset.enabled === 'true';
    const composed = composeExportBuffer(rgba, mask, {
      format,
      fillColor: wantsFill ? hexToRgb(fillColorInput.value) : undefined,
    });

    const blob = await encodeRgbaToBlob(composed, fileRaster.width, fileRaster.height, format, Number(jpegQualityInput.value));
    const suffix = suffixInput.value || '_nobg';
    const baseName = originalName.replace(/\.[^.]+$/, '');
    const ext = format === 'png' ? 'png' : 'jpg';
    return { filename: `${baseName}${suffix}.${ext}`, blob };
  }

  exportCurrentBtn.addEventListener('click', () => {
    if (!raster || !baseMask) return showToast('Load an image first', true);
    const active = fileManager.getActive();
    void withProgress('Exporting…', async () => {
      const { filename, blob } = await exportFile(raster!, baseMask!, active?.name ?? 'image.png');
      const saved = await downloadBlob(blob, filename);
      if (saved) announceSaved(exportCurrentBtn, filename, saved.where);
      else announceCancelled();
    }).catch((err) => showToast(err.message, true));
  });

  exportAllBtn.addEventListener('click', () => {
    const files = fileManager.getFiles();
    if (files.length < 2) return;
    void withProgress('Processing batch…', async (onProgress) => {
      const entries: { filename: string; data: Blob }[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const tempRaster = await loadRasterForExport(f);
        const detected = await autoSegment(tempRaster);
        const mask: MaskImage = detected ?? { width: tempRaster.width, height: tempRaster.height, alpha: new Uint8ClampedArray(tempRaster.width * tempRaster.height).fill(255) };
        const { filename, blob } = await exportFile(tempRaster, mask, f.name);
        entries.push({ filename, data: blob });
        onProgress(i + 1, files.length);
      }
      const zip = await buildZip(entries);
      const saved = await downloadBlob(zip, 'myphototools-nobg.zip');
      if (saved) announceSaved(exportAllBtn, 'myphototools-nobg.zip', saved.where);
      else announceCancelled();
    }).catch((err) => showToast(err.message, true));
  });

  async function loadRasterForExport(file: LoadedFile): Promise<RasterState> {
    if (file.sourceFormat === 'pdf') {
      const result = await dpiWorker.call<{ width: number; height: number; rgba: ArrayBuffer }[]>('rasterizePdf', {
        bytes: file.originalBytes.slice(0),
        dpi: 150,
        pageNumbers: [1],
      });
      const page = result[0];
      return { width: page.width, height: page.height, original: new Uint8ClampedArray(page.rgba), dpi: { x: 150, y: 150 } };
    }
    const blob = new Blob([file.originalBytes], { type: file.sourceFormat === 'png' ? 'image/png' : 'image/jpeg' });
    const decoded = await decodeImageToRgba(blob);
    return { width: decoded.width, height: decoded.height, original: decoded.rgba, dpi: null };
  }

  // Keyboard shortcuts scoped to this tab while it's visible.
  window.addEventListener('keydown', (e) => {
    if (panel.hidden) return;
    const mod = e.metaKey || e.ctrlKey;
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      undoBtn.click();
    } else if (mod && (e.key.toLowerCase() === 'z' || e.key.toLowerCase() === 'y') && e.shiftKey) {
      e.preventDefault();
      redoBtn.click();
    } else if (mod && e.key.toLowerCase() === 's') {
      e.preventDefault();
      exportCurrentBtn.click();
    } else if (!mod && e.key.toLowerCase() === 'b') {
      setActiveTool('eraseBrush');
    } else if (!mod && e.key.toLowerCase() === 'w') {
      setActiveTool('wand');
    } else if (!mod && e.key.toLowerCase() === 'h') {
      setActiveTool('move');
    }
  });
}

function buildMarkup(): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'workspace';
  wrapper.innerHTML = `
    <div class="canvas-column">
      <div class="canvas-toolbar" role="toolbar" aria-label="Background removal tools">
        <button id="tool-move" aria-pressed="false" title="Move (H) — drag to move the photo. With any tool you can also drag with the right mouse button, hold space, or (Wand / Color Key) just drag with the left button; scroll wheel or pinch to zoom.">Move</button>
        <button id="tool-wand" aria-pressed="false" title="Magic wand (W) — click a colour region to remove it, or (with Wand action = Restore) bring it back into the subject">Wand</button>
        <button id="tool-colorkey" aria-pressed="false" title="Color key (eyedropper)">Color Key</button>
        <button id="tool-erase" aria-pressed="false" title="Erase brush (B)">Erase</button>
        <button id="tool-restore" aria-pressed="false" title="Restore brush">Restore</button>
        <button id="go-bg" class="btn-go" hidden title="Remove the background of the loaded photo">GO</button>
        <button id="redetect-bg" title="Re-run automatic background removal, discarding manual touch-ups">Re-detect</button>
        <button id="retry-bg" title="Missed part of a person? Runs the detection several more ways (mirrored, padded, zoomed in on the subject) and combines them. Slower, but catches more.">Retry (thorough)</button>
        <button id="before-after-toggle" aria-pressed="false" title="Toggle before/after">Before/After</button>
        <button id="bg-undo" disabled title="Undo the last touch-up (⌘Z)">Undo</button>
        <button id="bg-redo" disabled title="Redo (⇧⌘Z)">Redo</button>
        <div class="zoom-controls">
          <button id="zoom-out-bg" title="Zoom out">−</button>
          <output id="zoom-level-bg" class="zoom-level">100%</output>
          <button id="zoom-in-bg" title="Zoom in">+</button>
          <button id="zoom-reset-bg" title="Fit the photo to the box again (resets zoom and position)">Reset</button>
        </div>
      </div>
      <div class="canvas-viewport canvas-area" aria-label="Image preview: click or tap to use the active tool; scroll wheel or pinch to zoom; drag to move the photo (with a brush active, drag with the right mouse button or hold space)"></div>
    </div>
    <aside class="side-panel">
      <div class="panel-section">
        <h2>Files</h2>
        <div id="bg-dropzone" class="dropzone">Drop PNG / JPEG / PDF here, or click to choose</div>
        <ul id="bg-file-list" class="file-list" aria-label="Loaded files"></ul>
        <div id="bg-dims-readout" class="dims-readout"></div>
      </div>

      <div class="panel-section">
        <h3>Automatic Cutout</h3>
        <div class="field-row">
          <label for="bg-quality">Quality</label>
          <select id="bg-quality" title="Best detail uses a larger model that handles hair, fur and fine edges; Fast uses a small model for quick previews">
            <option value="anime" selected>Illustration / anime</option>
            <option value="best">Best detail (photos)</option>
            <option value="fast">Fast</option>
          </select>
        </div>
        <p id="bg-quality-hint" class="hint" hidden>No GPU acceleration on this device, so Fast is selected; Best detail still works but takes much longer.</p>
        <div class="field-row">
          <label for="bg-detail" title="Lower keeps only the solid subject with clean, simple edges (removes faint strands, halos and fuzz); higher keeps every fine strand and semi-transparent edge">Detail kept</label>
          <input type="range" id="bg-detail" min="0" max="100" value="55" />
          <output id="bg-detail-out" for="bg-detail">55%</output>
        </div>
        <p class="hint">Low = clean, simple edges · High = keep fine hair and wisps. Re-applies the automatic cutout (manual touch-ups are reset).</p>
        <div class="field-row">
          <label for="bg-clean-edges" title="Compares each pixel near the edge with the nearby background and subject colours and fades out leftover background: glow between an arm and the body, scenery through gaps in hair, a halo hugging the outline">Clean leftover background near edges</label>
          <input type="checkbox" id="bg-clean-edges" checked />
        </div>
        <div class="field-row">
          <label for="bg-auto-start" title="Off: the photo loads and waits until you press GO. On: the background is removed the moment a photo is added.">Start automatically when a photo is added</label>
          <input type="checkbox" id="bg-auto-start" />
        </div>
      </div>

      <div class="panel-section">
        <h3>Tool Settings</h3>
        <div class="field-row">
          <label for="bg-tolerance">Tolerance</label>
          <input type="range" id="bg-tolerance" min="0" max="100" value="20" />
          <output id="bg-tolerance-out" for="bg-tolerance">20%</output>
        </div>
        <div class="field-row">
          <label id="wand-mode-label">Wand mode</label>
          <div id="wand-mode" class="segmented" role="group" aria-labelledby="wand-mode-label">
            <button data-mode="contiguous" aria-pressed="true">Contiguous</button>
            <button data-mode="global" aria-pressed="false">Global</button>
          </div>
        </div>
        <div class="field-row">
          <label id="wand-action-label" title="Remove: clicked colour region becomes transparent. Restore: it is brought back into the subject (fix a dropped dress, arm, etc.)">Wand action</label>
          <div id="wand-action" class="segmented" role="group" aria-labelledby="wand-action-label">
            <button data-action="remove" aria-pressed="true">Remove</button>
            <button data-action="restore" aria-pressed="false">Restore</button>
          </div>
        </div>
        <div class="field-row">
          <label for="brush-size">Brush size</label>
          <input type="range" id="brush-size" min="2" max="200" value="30" />
          <output id="brush-size-out" for="brush-size">30px</output>
        </div>
        <div class="field-row">
          <label for="brush-hardness">Brush hardness</label>
          <input type="range" id="brush-hardness" min="0" max="100" value="80" />
          <output id="brush-hardness-out" for="brush-hardness">80%</output>
        </div>
      </div>

      <div class="panel-section">
        <h3>Edge Quality</h3>
        <div class="field-row">
          <label for="feather">Feather</label>
          <input type="range" id="feather" min="0" max="10" value="0" />
          <output id="feather-out" for="feather">0px</output>
        </div>
        <div class="field-row">
          <label for="choke-expand">Choke / Expand</label>
          <input type="range" id="choke-expand" min="-20" max="20" value="-1" />
          <output id="choke-expand-out" for="choke-expand">-1px</output>
        </div>
        <div class="field-row">
          <label for="decontaminate">Remove color fringe</label>
          <input type="checkbox" id="decontaminate" checked />
        </div>
      </div>

      <div class="panel-section">
        <h3>Preview</h3>
        <div class="field-row">
          <label id="preview-bg-label">Background</label>
          <div id="preview-bg" class="segmented" role="group" aria-labelledby="preview-bg-label">
            <button data-bg="checkerboard" aria-pressed="true">Checker</button>
            <button data-bg="white" aria-pressed="false">White</button>
            <button data-bg="black" aria-pressed="false">Black</button>
            <button data-bg="custom" aria-pressed="false">Custom</button>
          </div>
        </div>
        <div class="field-row">
          <label for="preview-bg-custom">Custom color</label>
          <input type="color" id="preview-bg-custom" value="#ff00ff" />
        </div>
      </div>

      <div class="panel-section">
        <h3>Output</h3>
        <div class="field-row">
          <label for="bg-out-format">Format</label>
          <select id="bg-out-format">
            <option value="png">PNG (transparent)</option>
            <option value="jpeg">JPEG (filled)</option>
          </select>
        </div>
        <div class="field-row" id="bg-jpeg-quality-row" hidden>
          <label for="bg-jpeg-quality">JPEG quality</label>
          <input type="range" id="bg-jpeg-quality" min="0.5" max="1" step="0.01" value="0.92" />
          <output id="bg-jpeg-quality-out" for="bg-jpeg-quality">0.92</output>
        </div>
        <div class="field-row" id="bg-fill-color-row">
          <label for="bg-fill-color">Fill color</label>
          <input type="color" id="bg-fill-color" value="#ffffff" data-enabled="true" />
        </div>
        <div class="field-row">
          <label for="bg-suffix">Filename suffix</label>
          <input type="text" id="bg-suffix" value="_nobg" />
        </div>
        ${saveLocationMarkup('bg')}
        <div class="btn-row">
          <button id="bg-export-current" class="btn btn-primary">Export current (⌘S)</button>
          <button id="bg-export-all" class="btn" disabled>Process all → ZIP</button>
        </div>
      </div>
    </aside>
  `;
  return wrapper;
}
